import { omit } from "lodash-es";
import { useGetDesignHuddleAccessToken } from "../../modules/design-huddle/use-design-huddle-access-token";
import { SupabaseDesignHuddleCustomBrandService } from "../../modules/generic/server/supabase-account-service";
import { useSupabaseLoader } from "../../server/supabase/hooks";

import type { AugmentedTemplate } from "../../modules/multiformat/store/custom-templates-atom";
import type { Multiformat } from "../../modules/multiformat/store/types";

export interface CustomTemplates {
  [key: string]: AugmentedTemplate[];
}
export const useCustomTemplates = (
  multiformat: Multiformat | null,
): ReturnType<typeof useSupabaseLoader<CustomTemplates | null>> => {
  const getAccessToken = useGetDesignHuddleAccessToken();

  return useSupabaseLoader(
    async ({ supabase }) => {
      if (!multiformat) return null;

      const customBrandService = new SupabaseDesignHuddleCustomBrandService(supabase);
      const { data: customBrandData, error: customBrandError } =
        await customBrandService.maybeGet();

      if (customBrandError) throw new Error(customBrandError.message);

      if (!customBrandData || !customBrandData.brand_id) return null;

      DSHDLib.configure({
        access_token: await getAccessToken([]),
        visitor: false,
      });

      const abbreviations = multiformat.content_types.reduce<{ [abbr: string]: string }>(
        (acc, item) => {
          if (item.design_huddle?.abbreviation) acc[item.design_huddle.abbreviation] = item.name;
          return acc;
        },
        {},
      );

      const templates = await getCustomBrandTemplates(
        customBrandData.brand_id,
        abbreviations ?? {},
      );
      return templates;
    },
    [getAccessToken, multiformat],
  );
};

export async function getCustomBrandTemplates(
  brand_id: number,
  abbreviations: { [key: string]: string },
): Promise<CustomTemplates> {
  return new Promise((resolve, reject) => {
    const mappings: { [key: string]: string } = {
      ...abbreviations,
      LC: "Landscape Comms",
      SC: "Square Comms",
    };

    DSHDLib.getTemplates({ brand_id, search: "p0" }, (err, data) => {
      if (err) return reject(err);

      const templates = data.items
        .map((template) => {
          const [env, shortFormat, name] = template.template_title.split("_", 3);
          const isInvalid = [env, shortFormat, name].some(
            (v) => v === undefined || v === null || v.trim() === "",
          );
          if (isInvalid) {
            console.error("Invalid DH template title:", {
              templateTitle: template.template_title,
              env,
              shortFormat,
              name,
            });
            return null;
          }

          const format = mappings[shortFormat!] || shortFormat;

          return {
            env,
            type: ["LC", "SC"].includes(shortFormat!) ? "communication" : "multiformat",
            format: format!,
            name: name!,
            template,
            page_count: template.page_count,
            id: `custom-brand-template-${template.template_id}`,
          };
        })
        .filter((t) => t !== null)
        .filter(({ env }) => env === "p0")
        .toSorted((a, b) => a.name.localeCompare(b.name));

      resolve(
        templates.reduce<{ [key: string]: AugmentedTemplate[] }>((acc, { format, ...rest }) => {
          if (!acc[format]) acc[format] = [];
          acc[format].push(omit(rest, "env"));
          return acc;
        }, {}),
      );
    });
  });
}
